<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Individuals that have an injury, illness, or have had surgery that causes a loss of functional
      mobility, may benefit from the skilled services of a physiotherapist to help them restore
      normal mobility. The physiotherapist will assess their specific condition and prescribe
      treatments and exercises to help them restore their range of motion (ROM), strength, and
      mobility.
    </p>

    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
        <p class="mb-5">
          Sometimes, physiotherapists will use software to help them tailor a specific programme to
          their patient’s needs. In this activity, you will use a website developed for
          physiotherapists.
        </p>
        <p class="mb-5">
          This website is a free tool to help physiotherapists create exercise programs for people
          with injuries and disabilities. It has been developed by a team of physiotherapists
          employed by government-funded organisations in Sydney.
        </p>
        <p class="mb-5">
          The tool contains over 1,000 physiotherapy exercises suitable for various injuries and
          disabilities and aimed at improving strength, flexibility, function, and fitness. Very few
          of the exercises involve manual handling of the physiotherapist, most are exercises that
          can be done by the patient themselves at home with minimal equipment.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/3 w-full my-5">
        <img
          src="@/assets/images/experiences/physiotherapy/physio_activity_4_1.jpg"
          class="img-responsive center-item"
          alt="A physiotherapist measuring the range of motion of a bent knee with a goniometer."
        />
      </div>
    </div>

    <p class="mb-5">
      You can access this website now via the button below (it will open in a new tab and you will
      have to jump between these 2 tabs while completing the tasks.
    </p>

    <a href="https://www.physiotherapyexercises.com/" target="_blank">
      <centered-image-wrapper
        alt="Open the Physiotherapy Exercises website (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/physio_website_access.jpg')"
        classes="w-full"
      />
    </a>

    <vs-alert color="dark" title="Warning" active="true">
      You are not a physiotherapist - at least not yet! The exercises in this and later tasks are to
      improve your knowledge only. You should not be giving medical advice to anyone until you are
      fully qualified.
    </vs-alert>
    <h4 class="my-5">
      Task 1: Explore the website and create a booklet of exercises for a patient/client
    </h4>
    <p class="mb-5">
      Go to the Physiotherapy Exercises website (link above) and watch the introduction video.
    </p>

    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          You then need to use the website to create a booklet of 5-6 exercises that you could use
          with a patient with a particular injury or condition.
        </p>

        <p class="mb-5">
          The video on the right-hand side walks you through how to complete each step, so you
          really need to watch it.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: How to complete this task"
          @click="showVideoPopup(popupVideo)"
        >
          <img
            src="@/assets/images/experiences/physiotherapy/how_to_complete_task_video.jpg"
            class="img-responsive center-item cursor-pointer"
            alt=""
          />
        </button>
      </div>
    </div>

    <p class="mb-5">
      Once you have created your exercise booklet, you need to download it then upload it to this
      task below.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Exercise Booklet'"
      class="mb-5"
      filetype="photo_doc"
      index="1"
    />

    <vs-alert color="dark" title="Warning" active="true">
      Don’t do any exercises that cause you or your volunteer pain. Everything should be done slowly
      and safely. Don’t manipulate your volunteer’s body with your hands. All exercises should be
      performed by the person themselves and nothing should be forced.
    </vs-alert>

    <h4 class="my-5">Task 2: Teach one exercise to another person</h4>
    <p class="mb-5">
      Choose one of the exercises that you included in the booklet you created. Find a volunteer
      (friend of family member) and teach them this exercise.
    </p>

    <p class="mb-5">
      i) Details of this exercise (you can get these from your downloaded exercise booklet)
    </p>

    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/6 lg:w-1/5 w-full my-5">
        <p class="py-0 pr-2 mb-2">Name of exercise:</p>
      </div>
      <div class="vx-col xl:w-5/6 lg:w-4/5 w-full my-5">
        <text-block v-model="data.question1" :editable="isEditable" class="w-full" />
      </div>
    </div>
    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/6 lg:w-1/5 w-full my-5">
        <p class="py-0 pr-2 mb-2">Therapist’s aim:</p>
      </div>
      <div class="vx-col xl:w-5/6 lg:w-4/5 w-full my-5">
        <textarea-block v-model="data.question2" :editable="isEditable" class="w-full" />
      </div>
    </div>
    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/6 lg:w-1/5 w-full my-5">
        <p class="py-0 pr-2 mb-2">Client’s aim:</p>
      </div>
      <div class="vx-col xl:w-5/6 lg:w-4/5 w-full my-5">
        <textarea-block v-model="data.question3" :editable="isEditable" class="w-full" />
      </div>
    </div>
    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/6 lg:w-1/5 w-full my-5">
        <p class="py-0 pr-2 mb-2">Therapist's instructions:</p>
      </div>
      <div class="vx-col xl:w-5/6 lg:w-4/5 w-full my-5">
        <textarea-block v-model="data.question4" :editable="isEditable" class="w-full" />
      </div>
    </div>
    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/6 lg:w-1/5 w-full my-5">
        <p class="py-0 pr-2 mb-2">Client’s instructions:</p>
      </div>
      <div class="vx-col xl:w-5/6 lg:w-4/5 w-full my-5">
        <textarea-block v-model="data.question5" :editable="isEditable" class="w-full" />
      </div>
    </div>
    <p class="mb-5">ii) Upload a video of your volunteer patient performing this exercise.</p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Video'"
      class="mb-5"
      filetype="video"
      index="2"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data: function () {
    return {
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
      popupVideo:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/physiotherapy/physio_activity_4.mp4',
    };
  },
  methods: {
    ...methods,
  },
  computed: {
    ...computed,
  },
  inject: inject,
  mounted() {
    for (let i = 1; i <= 4; i++) {
      if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
        this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
      }
    }
  },
};
</script>
